<template>
  <div class="media-input">
    <!-- FILES INPUT -->
    <input
      type="file"
      class="form-control-file"
      multiple
      accept="image/x-png,image/gif,image/jpeg"
      @change="onFileSelected"
      id="mediaInput"
    />
    <!-- PROGRESS BAR -->
    <div class="progress mt-2" style="height: 4px;">
      <div class="progress-bar" id="progressBar" role="progressbar" style="width: 0%;" aria-valuemin="0" aria-valuemax="100"></div>
    </div>
    <!-- PREVIEW IMAGES -->
    <div
      v-for="(thumb, i) in thumbs"
      v-bind:key="i"
      class="preview-image-container"
    >
      <div @click="deleteMedia(thumb, i)" class="preview-image-delete clickable">
        <i class="far fa-times"></i>
      </div><!-- /.preview-image-delete -->
      <img :src="thumb.url" width="100px">
      <input
        v-model="thumb.caption"
        class="form-control form-control-sm mt-1"
        placeholder="Caption"
        @input="updateCaption(i, thumb.caption)"
      />
      <div class="mt-1">
        <button
          type="button"
          class="btn btn-sm btn-outline-light mr-1"
          @click="moveImage(i, -1)"
          :disabled="i === 0"
        >
          ↑
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-light"
          @click="moveImage(i, 1)"
          :disabled="i === thumbs.length - 1"
        >
          ↓
        </button>
      </div>
    </div><!-- /.preview-image-container -->
  </div><!-- /.media-input -->
</template>

<script>
import firebase from 'firebase/compat/app'

export default {
  data () {
    return {
      mediaPayload: [],
      thumbs: [],
      syncRunId: 0,
      lastMediaPaths: ''
    }
  },
  props: {
    mediaObject: { type: Array, default: () => [] },
    blockUpload: { type: Boolean, default: false },
    /** One image per log entry; new selection replaces current. */
    singleImageForLogEntry: { type: Boolean, default: false }
  },
  computed: {
    effectiveDatasetId () {
      const c = this.$store.state.collector
      return (c && (c.datasetId || c.id)) || 'dynamic'
    }
  },
  created () {
    this.syncFromMediaObject()
  },
  watch: {
    mediaObject: {
      handler (newVal) {
        const paths = (Array.isArray(newVal) ? newVal : [])
          .map(m => m && m.path)
          .filter(Boolean)
          .join(',')
        if (paths === this.lastMediaPaths) return
        this.lastMediaPaths = paths
        this.syncFromMediaObject()
      },
      deep: true
    }
  },
  methods: {
    syncFromMediaObject () {
      this.syncRunId += 1
      const currentRun = this.syncRunId
      const list = Array.isArray(this.mediaObject) ? this.mediaObject : []
      this.lastMediaPaths = list.map(m => m && m.path).filter(Boolean).join(',')
      this.mediaPayload = [...list]
      // Initialize thumbs array in the same order as mediaPayload
      this.thumbs = this.mediaPayload.map(mediaItem => ({
        url: null,
        path: mediaItem && mediaItem.path,
        small: mediaItem && mediaItem.small,
        medium: mediaItem && mediaItem.medium,
        large: mediaItem && mediaItem.large,
        caption: mediaItem && mediaItem.caption ? mediaItem.caption : ''
      }))

      this.mediaPayload.forEach((mediaItem, index) => {
        const self = this
        if (!mediaItem || !mediaItem.path) return
        const ref = firebase.storage().ref(mediaItem.path)
        ref.getDownloadURL().then(url => {
          if (currentRun !== self.syncRunId) return
          if (!self.thumbs[index]) return
          self.$set(self.thumbs[index], 'url', url)
        }).catch(() => {
          if (mediaItem.small && mediaItem.small !== mediaItem.path) {
            firebase.storage().ref(mediaItem.small).getDownloadURL().then(url => {
              if (currentRun !== self.syncRunId) return
              if (!self.thumbs[index]) return
              self.$set(self.thumbs[index], 'url', url)
            }).catch(() => {})
          }
        })
      })
    },
    updateCaption (index, caption) {
      if (!this.mediaPayload || !this.mediaPayload.length || !this.mediaPayload[index]) return
      const updated = { ...this.mediaPayload[index], caption }
      this.$set(this.mediaPayload, index, updated)
      this.$emit('mediaDelivery', this.mediaPayload)
    },
    moveImage (index, delta) {
      const newIndex = index + delta
      if (newIndex < 0 || newIndex >= this.thumbs.length) return
      const thumbItem = this.thumbs.splice(index, 1)[0]
      this.thumbs.splice(newIndex, 0, thumbItem)
      const mediaItem = this.mediaPayload.splice(index, 1)[0]
      this.mediaPayload.splice(newIndex, 0, mediaItem)
      this.$emit('mediaDelivery', this.mediaPayload)
    },
    onFileSelected (e) {
      const self = this
      function getFormattedTime () {
        var today = new Date()
        var y = today.getFullYear()
        var m = today.getMonth() + 1
        var d = today.getDate()
        return y + '-' + m + '-' + d + '-'
      }
      function s4 () {
        return Math.floor((1 + Math.random()) * 0x10000)
          .toString(16)
          .substring(1)
      }

      if (this.singleImageForLogEntry) {
        this.mediaPayload = []
        this.thumbs = []
      }

      const fileList = e.target.files || []
      const numToProcess = this.singleImageForLogEntry ? Math.min(1, fileList.length) : fileList.length
      for (var i = 0; i < numToProcess; i++) {
        const mediaId = getFormattedTime() + s4()
        const file = fileList[i]
        const fileName = mediaId + file.name
        const smallFileName = fileName.replace(/(\.[\w\d_-]+)$/i, '_500x500$1')
        const mediumFileName = fileName.replace(/(\.[\w\d_-]+)$/i, '_1000x1000$1')
        const largeFileName = fileName.replace(/(\.[\w\d_-]+)$/i, '_2000x2000$1')
        let mediaPath, smallMediaPath, mediumMediaPath, largeMediaPath
        if (this.blockUpload && this.$store.state.farm && this.$store.state.farm.id) {
          mediaPath = 'farms/' + this.$store.state.farm.id + '/images/' + fileName
          smallMediaPath = 'farms/' + this.$store.state.farm.id + '/images/thumbs/' + smallFileName
          mediumMediaPath = 'farms/' + this.$store.state.farm.id + '/images/thumbs/' + mediumFileName
          largeMediaPath = 'farms/' + this.$store.state.farm.id + '/images/thumbs/' + largeFileName
        } else {
          const datasetId = this.effectiveDatasetId
          mediaPath = 'datasets/dynamic/' + datasetId + '/images/' + fileName
          smallMediaPath = 'datasets/dynamic/' + datasetId + '/images/thumbs/' + smallFileName
          mediumMediaPath = 'datasets/dynamic/' + datasetId + '/images/thumbs/' + mediumFileName
          largeMediaPath = 'datasets/dynamic/' + datasetId + '/images/thumbs/' + largeFileName
        }
        this.mediaPayload.push({
          path: mediaPath,
          small: smallMediaPath,
          medium: mediumMediaPath,
          large: largeMediaPath,
          caption: ''
        })
        // Set the Firebase storage reference for this media item
        const storageRef = firebase.storage().ref(mediaPath)
        // Upload the file to Firebase with task variable.
        // Tag customMetadata.userId so the usageFinalize backend trigger
        // can attribute bytes to the right user. Without this, byte
        // tracking falls back to "skip" (we'd rather under-count than
        // mis-attribute).
        const uploadMetadata = {
          customMetadata: {
            userId: (this.$store.state.currentUser && this.$store.state.currentUser.uid) || ''
          }
        }
        const task = storageRef.put(file, uploadMetadata)
        // Listen for state changes as media item is being uploaded
        task.on('state_changed', function progress (snapshot) {
          // Set percentage of the progress bat
          // todo - Make this progress bar smarter to take account of all media items being uploaded at the same time.
          const percentage = (snapshot.bytesTransferred / snapshot.totalBytes) * 100 + '%'
          document.getElementById('progressBar').style.width = percentage
        },
        function error (err) {
          console.log('errors: ' + err)
        },
        function complete () {
          document.getElementById('progressBar').style.width = 0
          firebase.storage().ref(mediaPath).getDownloadURL().then(url => {
            self.thumbs.push({
              url: url,
              path: mediaPath,
              small: smallMediaPath,
              medium: mediumMediaPath,
              large: largeMediaPath,
              caption: ''
            })
            self.$emit('mediaDelivery', self.mediaPayload)
          })
        })
      }
    },
    deleteMedia (media, i) {
      firebase.storage().ref(media.path).delete().catch(() => {})
      if (media.small && media.small !== media.path) {
        firebase.storage().ref(media.small).delete().catch(() => {})
      }
      if (media.medium && media.medium !== media.path) {
        firebase.storage().ref(media.medium).delete().catch(() => {})
      }
      if (media.large && media.large !== media.path) {
        firebase.storage().ref(media.large).delete().catch(() => {})
      }
      this.thumbs.splice(i, 1)
      this.mediaPayload.splice(i, 1)
      this.$emit('mediaDelivery', this.mediaPayload)
    }
  }
}
</script>

<style scoped>
.preview-image-container {
  position: relative;
  display: inline-block;
  padding: 10px;
  background-color: #333;
  margin: 10px;
  border-radius: 4px;
}
.preview-image-delete {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: red;
  border-radius: 50px;
  padding: 0px 7px
}
</style>
